import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './CartridgeForm.css';

interface CartridgeFormProps {
  user: { id: string; email: string; role: string };
}

const BED_SIZES = [
  'Small (Community Hospital)',
  'Medium (Regional Medical Center)',
  'Large (Teaching Hospital / Health System)',
  'IDN / Multi-site Health System',
  'ASC / Outpatient Center',
];

const PERSONA_TYPES = [
  'CFO / Finance',
  'VP Oncology / Service Line',
  'Chief Medical Physicist',
  'Director of Radiation Therapy',
  'Urologist (Academic Medical Center)',
  'Urologist (Private Practice)',
  'OR Director',
  'Chief Medical Officer',
  'VP Supply Chain',
  'Other',
];

const TONE_LABELS: Record<number, string> = {
  1: 'Very Collaborative',
  2: 'Collaborative',
  3: 'Neutral',
  4: 'Challenging',
  5: 'Very Challenging',
};

const CartridgeForm: React.FC<CartridgeFormProps> = ({ user }) => {
  const navigate = useNavigate();

  const [accountName, setAccountName] = useState('');
  const [bedSize, setBedSize] = useState('');
  const [personaType, setPersonaType] = useState('');
  const [practiceIssue, setPracticeIssue] = useState('');
  const [tone, setTone] = useState(3);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setGenerating(true);

    try {
      const response = await fetch('/api/cartridge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accountName,
          bedSize,
          personaType,
          practiceIssue,
          tone,
          userId: user.id,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.simulationId) {
        throw new Error(data.error || 'Failed to generate scenario. Please try again.');
      }

      navigate(`/simulation/${data.simulationId}`);
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred.');
      setGenerating(false);
    }
  };

  if (generating) {
    return (
      <div className="cartridge-container">
        <div className="cartridge-card">
          <div className="generating-state">
            <div className="cartridge-spinner" />
            <p>Generating your scenario...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cartridge-container">
      <div className="cartridge-card">
        <div className="cartridge-header">
          <h1 className="cartridge-title">Build Your Scenario</h1>
          <p className="cartridge-subtitle">
            Define the account, persona, and challenge — your custom AI buyer will be ready in seconds.
          </p>
        </div>

        <div className="cartridge-body">
          {error && (
            <div className="cartridge-error" role="alert">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="field-group">
              <label className="field-label" htmlFor="accountName">
                Account Name
              </label>
              <input
                id="accountName"
                type="text"
                className="form-input"
                placeholder="e.g. Regional Medical Center"
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
                required
              />
            </div>

            <div className="field-group">
              <label className="field-label" htmlFor="bedSize">
                Hospital / Practice Size
              </label>
              <select
                id="bedSize"
                className="form-input form-select"
                value={bedSize}
                onChange={(e) => setBedSize(e.target.value)}
                required
              >
                <option value="">Select account size...</option>
                {BED_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>

            <div className="field-group">
              <label className="field-label" htmlFor="personaType">
                Who are you meeting with?
              </label>
              <select
                id="personaType"
                className="form-input form-select"
                value={personaType}
                onChange={(e) => setPersonaType(e.target.value)}
                required
              >
                <option value="">Select persona...</option>
                {PERSONA_TYPES.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>

            <div className="field-group">
              <label className="field-label" htmlFor="practiceIssue">
                What do you want to practice?
              </label>
              <textarea
                id="practiceIssue"
                className="form-input form-textarea"
                placeholder="e.g. Overcoming a price objection on capital equipment, handling a competitor comparison, gaining access to the C-suite..."
                value={practiceIssue}
                onChange={(e) => setPracticeIssue(e.target.value)}
                rows={4}
                required
              />
            </div>

            <div className="field-group">
              <label className="field-label">
                Persona Tone
                <span className="tone-value-label">{TONE_LABELS[tone]}</span>
              </label>
              <input
                type="range"
                className="tone-slider"
                min={1}
                max={5}
                step={1}
                value={tone}
                onChange={(e) => setTone(Number(e.target.value))}
                aria-label="Persona tone"
              />
              <div className="tone-labels">
                <span>Collaborative</span>
                <span>Challenging</span>
              </div>
            </div>

            <button type="submit" className="generate-btn">
              Generate Scenario
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CartridgeForm;
